import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { AlternativeTransportElement } from "features/VehicleSidebar/VehicleList/AlternativeTransportList/AlternativeTransportElement";
import { useAlternativeTransportData } from "features/VehicleSidebar/VehicleList/AlternativeTransportList/useAlternativeTransportData";
import { useVyatData } from "features/VehicleSidebar/VehicleList/AlternativeTransportList/useVyAtData";
import RenderNoDataMessage, {
  RenderMessage,
} from "features/VehicleSidebar/VehicleList/common/RenderNoDataMessage";
import { ResultList } from "features/VehicleSidebar/VehicleList/common/ResultList";
import { ResultListWrapper } from "features/VehicleSidebar/VehicleList/common/ResultListWrapper";
import { useFilter } from "features/VehicleSidebar/VehicleList/Filtering/useFilter";
import { filterAlternativeTransport } from "features/VehicleSidebar/VehicleList/Filtering/vehicleFiltering";
import { VehicleSearchFilter } from "features/VehicleSidebar/VehicleList/Filtering/VehicleSearchFilter";
import { FC, RefObject, useMemo } from "react";
import { generatePath, useLocation } from "react-router-dom";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { useInfiniteScroll } from "shared/hooks/useInfiniteScroll";
import { getDate } from "shared/utils/dateTimeUtils";
import { BUS } from "shared/utils/routes";
import { processTransportList } from "features/VehicleSidebar/VehicleList/AddVehicles/utils";

type AlternativeTransportListProps = {
  searchQuery: string;
  selectedDate: Date;
  showOldVehicles: boolean;
  scrollBoxRef: RefObject<HTMLDivElement | null>;
};

type Location = {
  latitude: string;
  longitude: string;
  timestamp: string;
};

type OperationalVehicle = {
  vehicleId: string;
  latestLocation: Location;
};

export type OperationalVehiclesCollection = {
  operationalVehicles: OperationalVehicle[];
};

type BusLocation = {
  latitude: number;
  longitude: number;
  timestamp: string;
  sessionId: string;
};

type OperationalVehicleGPS = {
  vehicleId: string;
  latestLocation: Location;
  workshiftId: string;
  trainNumber: string;
  phoneNumber: string;
  timestamp: string;
  busLocation: BusLocation;
  path: any[];
};

export type OperationalVehicleGPSCollection = {
  operationalVehicles: OperationalVehicleGPS[];
};

const AlternativeTransportList: FC<AlternativeTransportListProps> = ({
  searchQuery,
  selectedDate,
  showOldVehicles,
  scrollBoxRef,
}) => {
  const day = getDate(selectedDate);
  const { pathname } = useLocation();

  const { status, data } = useAlternativeTransportData(day);

  const { vyAtData } = useVyatData();

  const filterOptions = ["Skiftnr", "Stasjon", "Telefonnr", "Tognr"];

  const {
    selectedFilter,
    handleFilterChange,
    filterOptions: options,
  } = useFilter(filterOptions);

  const alternativeTransportVehicleList =
    status === "success" && data ? data.vehicles : [];

  const processedList = processTransportList({
    transportList: alternativeTransportVehicleList,
    showOldVehicles,
    timeKey: "scheduledArrivalTimeDestination",
  });

  const filteredAlternativeTransportList = useMemo(
    () =>
      processedList.filter((alternativeTransport) =>
        filterAlternativeTransport(
          alternativeTransport,
          searchQuery,
          selectedFilter,
        ),
      ),
    [
      processedList,
      searchQuery,
      selectedFilter,
      filterAlternativeTransport,
      showOldVehicles,
    ],
  );

  const { numberOfItemsToRender } = useInfiniteScroll({
    containerRef: scrollBoxRef,
    initialItemsToRender: 20,
    maxItemsToRender: filteredAlternativeTransportList.length,
  });

  const listToShow =
    searchQuery.length > 0 ? filteredAlternativeTransportList : processedList;

  switch (status) {
    case "pending":
      return <SkeletonLoader skeletonType="trainList" />;
    case "success":
      return (
        <>
          <VehicleSearchFilter
            searchQuery={searchQuery}
            resultLength={filteredAlternativeTransportList.length}
            filterOptions={options}
            selectedFilter={selectedFilter}
            handleFilterChange={handleFilterChange}
          />

          <ResultListWrapper>
            <ResultList>
              {listToShow.length > 0 ? (
                listToShow
                  .slice(0, numberOfItemsToRender)
                  .map((alternativeTransport) => (
                    <AlternativeTransportElement
                      key={alternativeTransport.id}
                      alternativeTransport={alternativeTransport}
                      elementSelected={
                        pathname ===
                        generatePath(BUS, {
                          vehicleUuid: alternativeTransport.id,
                        })
                      }
                      hasTracking={
                        vyAtData?.operationalVehicles.some(
                          (it) => it.vehicleId === alternativeTransport.id,
                        ) ?? false
                      }
                    />
                  ))
              ) : (
                <RenderNoDataMessage
                  processedList={processedList}
                  renderMessage={RenderMessage.Alternative}
                />
              )}
            </ResultList>
          </ResultListWrapper>
        </>
      );
    case "error":
      return <FailureMessage style={{ margin: "12px" }} />;
  }
};

export default withErrorBoundary(AlternativeTransportList);
[1, 3].some((el) => el === 2);
